import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Camera, Lock, User, Phone, Loader2, Check, Repeat } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// Mirrors App.js's CLIENT_ROUTE_ROLE_OVERRIDES / AuthRedirect role→dashboard
// mapping, kept local here since this modal is shared across every
// dashboard and doesn't otherwise depend on App.js.
const ROLE_LABELS = {
  admin: 'Admin',
  hub_manager: 'Hub Manager',
  cluster_manager: 'Cluster Manager',
  center_manager: 'Center Manager',
  sales: 'Sales',
  client: 'Client',
  founders: 'Founders',
  finance_audit: 'Finance & Audit',
  manager: 'Approvals Manager',
  finance: 'Finance Approvals',
  landlord: 'Landlord',
  bd_transactions: 'BD Transactions',
};

const ROLE_DASHBOARD_PATH = {
  admin: '/admin',
  hub_manager: '/admin',
  center_manager: '/manager',
  cluster_manager: '/manager',
  sales: '/sales',
  founders: '/founders',
  finance_audit: '/finance',
  manager: '/approvals',
  finance: '/finance-approvals',
  landlord: '/landlord',
  bd_transactions: '/bd-transactions',
  client: '/dashboard',
};

export const ProfileModal = ({ open, onClose, user, api, onProfileUpdate }) => {
  const { switchRole } = useAuth();
  const [switchingRole, setSwitchingRole] = useState(false);
  const [activeSection, setActiveSection] = useState('profile');
  const [name, setName] = useState(user?.name || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [photoUrl, setPhotoUrl] = useState(user?.photo_url || '');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  // Password fields
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/upload/images', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const url = res.data.url || res.data.file_url;
      setPhotoUrl(url);
      toast.success('Photo uploaded');
    } catch (err) {
      toast.error('Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (name.trim() && name.trim() !== user?.name) payload.name = name.trim();
      if (phone !== (user?.phone || '')) payload.phone = phone;
      if (photoUrl !== (user?.photo_url || '')) payload.photo_url = photoUrl;

      if (Object.keys(payload).length === 0) {
        toast.info('No changes to save');
        setSaving(false);
        return;
      }

      await api.patch('/auth/profile', payload);
      toast.success('Profile updated');
      onProfileUpdate?.(payload);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword) {
      toast.error('Enter your current password');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setChangingPassword(true);
    try {
      await api.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSwitchRole = async (newRole) => {
    if (newRole === user?.role || switchingRole) return;
    setSwitchingRole(true);
    try {
      await switchRole(newRole);
      toast.success(`Switched to ${ROLE_LABELS[newRole] || newRole} mode`);
      // Hard navigation so the new dashboard's own state/queries load fresh,
      // consistent with the hard-redirect pattern used elsewhere in this app
      // (see AuthContext's 401 handler).
      window.location.href = ROLE_DASHBOARD_PATH[newRole] || '/dashboard';
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to switch role');
      setSwitchingRole(false);
    }
  };

  const grantedRoles = user?.granted_roles || [];
  const canSwitchRole = grantedRoles.length > 1;

  const initial = (user?.name || 'U').charAt(0).toUpperCase();

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="profile-modal">
        <DialogHeader>
          <DialogTitle className="font-heading">My Profile</DialogTitle>
        </DialogHeader>

        {canSwitchRole && (
          <div className="rounded-lg border border-border bg-muted/40 p-3 mb-4" data-testid="profile-role-switcher">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-2">
              <Repeat className="w-3.5 h-3.5" />
              Active Role
            </div>
            <div className="flex flex-wrap gap-2">
              {grantedRoles.map((r) => (
                <button
                  key={r}
                  onClick={() => handleSwitchRole(r)}
                  disabled={switchingRole}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
                    r === user?.role
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background text-foreground border-border hover:border-primary/50'
                  }`}
                  data-testid={`profile-role-switch-${r}`}
                >
                  {ROLE_LABELS[r] || r}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Section Tabs */}
        <div className="flex gap-1 border-b border-border pb-0 mb-4">
          <button
            onClick={() => setActiveSection('profile')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeSection === 'profile'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="profile-tab-profile"
          >
            <User className="w-4 h-4 inline mr-1.5" />
            Profile
          </button>
          <button
            onClick={() => setActiveSection('password')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeSection === 'password'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="profile-tab-password"
          >
            <Lock className="w-4 h-4 inline mr-1.5" />
            Password
          </button>
        </div>

        {activeSection === 'profile' && (
          <div className="space-y-5">
            {/* Photo */}
            <div className="flex flex-col items-center gap-3">
              <div className="relative group">
                {photoUrl ? (
                  <img
                    src={photoUrl.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${photoUrl}` : photoUrl}
                    alt="Profile"
                    className="w-20 h-20 rounded-full object-cover border-2 border-primary/20"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center border-2 border-primary/20">
                    <span className="text-2xl font-bold text-primary">{initial}</span>
                  </div>
                )}
                <button
                  onClick={() => fileRef.current?.click()}
                  className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                  disabled={uploading}
                >
                  {uploading ? (
                    <Loader2 className="w-5 h-5 text-white animate-spin" />
                  ) : (
                    <Camera className="w-5 h-5 text-white" />
                  )}
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handlePhotoUpload}
                />
              </div>
              <p className="text-xs text-muted-foreground">Click to change photo</p>
            </div>

            {/* Name */}
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                data-testid="profile-name-input"
              />
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Phone number"
                data-testid="profile-phone-input"
              />
            </div>

            {/* Email (read-only) */}
            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={user?.email || ''} disabled className="opacity-60" />
            </div>

            <Button
              onClick={handleSaveProfile}
              disabled={saving}
              className="w-full bg-primary hover:bg-primary/90"
              data-testid="profile-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </div>
        )}

        {activeSection === 'password' && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Current Password</Label>
              <Input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                data-testid="profile-current-password"
              />
            </div>
            <div className="space-y-2">
              <Label>New Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 6 chars)"
                data-testid="profile-new-password"
              />
            </div>
            <div className="space-y-2">
              <Label>Confirm New Password</Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                data-testid="profile-confirm-password"
              />
            </div>
            <Button
              onClick={handleChangePassword}
              disabled={changingPassword}
              className="w-full bg-primary hover:bg-primary/90"
              data-testid="profile-change-password-btn"
            >
              {changingPassword ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
              Change Password
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
